(() => {
  'use strict';

  const MANIFEST_PROPERTY = '_tm_studio_preview_manifest';
  const LEGACY_MANIFEST_PROPERTY = '_studio_cart_preview_manifest';
  const MAX_VIEWS = 8;
  const MAX_ASSETS = 24;

  const asText = (value) => String(value || '').trim();
  const isObject = (value) => Boolean(value && typeof value === 'object' && !Array.isArray(value));
  const isInactive = (value) => value === false || value === 'false';

  const normalizeImageUrl = (value) => {
    const url = asText(value);
    if (/^(https?:)?\/\//i.test(url) || /^\/(?!\/)/u.test(url)) return url;
    return '';
  };

  const roundMetric = (value) => Math.round(Number(value) * 1000000) / 1000000;

  const parseJsonObject = (value) => {
    if (isObject(value)) return value;
    if (!asText(value)) return null;
    try {
      const parsed = JSON.parse(value);
      return isObject(parsed) ? parsed : null;
    } catch (error) {
      return null;
    }
  };

  const normalizeReference = (value) => {
    let reference = asText(value).toLowerCase();
    if (typeof reference.normalize === 'function') {
      reference = reference.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }
    const aliases = {
      face: 'front',
      devant: 'front',
      dos: 'back',
      rear: 'back',
      coeur: 'heart',
      poitrine: 'chest',
      manche: 'sleeve',
      manche_gauche: 'left_sleeve',
      manche_droite: 'right_sleeve',
    };
    reference = reference.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    return aliases[reference] || reference;
  };

  const referencesMatch = (reference, candidates = []) => {
    const target = normalizeReference(reference);
    if (!target) return false;
    return candidates.some((candidate) => normalizeReference(candidate) === target);
  };

  const parseCartPreviewManifest = (properties = {}) => {
    const rawManifest = properties && (properties[MANIFEST_PROPERTY] || properties[LEGACY_MANIFEST_PROPERTY]);
    const manifest = parseJsonObject(rawManifest);
    if (!manifest || ![1, 2].includes(Number(manifest.version)) || !Array.isArray(manifest.views)) return null;
    return manifest;
  };

  const getVariantLookupKeys = (variantId) => {
    const value = asText(variantId);
    const numericId = (value.match(/(\d+)(?:\D*)$/u) || [])[1] || '';
    return Array.from(new Set([
      value,
      numericId,
      numericId ? `gid://shopify/ProductVariant/${numericId}` : '',
    ].filter(Boolean)));
  };

  const normalizeZone = (zone = {}, fallbackId = '') => {
    if (!isObject(zone)) return null;
    const x = Number(zone.x);
    const y = Number(zone.y);
    const width = Number(zone.width);
    const height = Number(zone.height);
    if (![x, y, width, height].every(Number.isFinite) || width <= 0 || height <= 0) return null;
    return {
      ...zone,
      id: asText(zone.id || zone.key || fallbackId),
      placementId: asText(zone.placementId || zone.groupId || zone.placement_id || zone.group_id),
      x,
      y,
      width,
      height,
    };
  };

  const getPartBounds = (parts = []) => {
    const validParts = (Array.isArray(parts) ? parts : []).map((part) => normalizeZone(part)).filter(Boolean);
    if (!validParts.length) return null;
    const left = Math.min(...validParts.map((part) => part.x));
    const top = Math.min(...validParts.map((part) => part.y));
    const right = Math.max(...validParts.map((part) => part.x + part.width));
    const bottom = Math.max(...validParts.map((part) => part.y + part.height));
    return { x: left, y: top, width: right - left, height: bottom - top };
  };

  const normalizeViewOption = (view = {}, index = 0) => {
    if (!isObject(view)) return null;
    const key = asText(view.key || view.viewId || view.id || `view-${index + 1}`);
    const sourceViewId = asText(view.sourceViewId || view.viewId || view.view || view.id || key);
    const image = normalizeImageUrl(view.image || view.imageUrl || view.src || view.fallbackImage);
    const rawZones = Array.isArray(view.zones) ? view.zones : (view.zone ? [view.zone] : []);
    const zones = rawZones.map((zone, zoneIndex) => normalizeZone(zone, `${key}-zone-${zoneIndex + 1}`)).filter(Boolean);
    if (!key || !image) return null;
    return {
      key,
      viewId: key,
      sourceViewId,
      zoneId: asText(view.zoneId || zones[0]?.placementId || zones[0]?.id),
      label: asText(view.label || key),
      alt: asText(view.alt || view.label || key),
      image,
      directCompositeSrc: normalizeImageUrl(view.directCompositeSrc),
      ...Object.fromEntries(['kind', 'previewMode', 'photoPreviewMode'].filter(key => view[key] !== undefined).map(key => [key, view[key]])),
      zones,
    };
  };

  const getMappedViewId = (config, placement, variantId) => {
    const variantMedia = isObject(config.variantMediaV2) ? config.variantMediaV2 : (
      isObject(config.variantMedia) ? config.variantMedia : {}
    );
    const mappingKey = getVariantLookupKeys(variantId).find((key) => isObject(variantMedia[key]));
    const assignments = mappingKey ? variantMedia[mappingKey] : null;
    if (!assignments) return '';
    const placementReferences = [
      placement?.id,
      ...(Array.isArray(placement?.legacyKeys) ? placement.legacyKeys : []),
    ].map(asText).filter(Boolean);
    const assignmentKey = Object.keys(assignments).find((key) => (
      placementReferences.some((reference) => referencesMatch(key, [reference]))
    ));
    const mapped = assignmentKey ? assignments[assignmentKey] : null;
    return asText(mapped?.viewId || mapped);
  };

  const buildViewOptionsFromConfig = (rawConfig = {}, variantId = '') => {
    if (!isObject(rawConfig)) return [];
    const placements = Array.isArray(rawConfig.placements) ? rawConfig.placements : [];
    const mediaViews = Array.isArray(rawConfig.mediaViews) ? rawConfig.mediaViews : [];
    const adaptedViews = Array.isArray(rawConfig.views) ? rawConfig.views : [];
    const adaptedZones = Array.isArray(rawConfig.zones) ? rawConfig.zones : [];
    if (!placements.length) return adaptedViews.map(normalizeViewOption).filter(Boolean);

    return placements.slice(0, MAX_VIEWS).reduce((options, placement, placementIndex) => {
      if (!isObject(placement) || isInactive(placement.active)) return options;
      const placementId = asText(placement.id || `placement-${placementIndex + 1}`);
      const instances = Array.isArray(placement.instances) ? placement.instances : [];
      const mappedViewId = getMappedViewId(rawConfig, placement, variantId);
      let instance = mappedViewId
        ? instances.find((candidate) => asText(candidate?.viewId) === mappedViewId)
        : null;
      if (!instance && mappedViewId) return options;
      if (!instance) instance = instances[0];

      const sourceViewId = asText(instance?.viewId || mappedViewId);
      const view = adaptedViews.find((candidate) => asText(candidate?.key || candidate?.id) === sourceViewId) ||
        mediaViews.find((candidate) => asText(candidate?.id || candidate?.key) === sourceViewId);
      if (!view) return options;

      const adaptedZone = adaptedZones.find((candidate) => (
        !isInactive(candidate?.active) &&
        referencesMatch(placementId, [candidate?.placementId, candidate?.groupId]) &&
        (!sourceViewId || referencesMatch(sourceViewId, [candidate?.view]))
      ));
      const instanceBounds = normalizeZone(instance, asText(instance?.id)) || getPartBounds(instance?.parts);
      const zone = normalizeZone(adaptedZone || (instanceBounds ? {
        ...instanceBounds,
        ...Object.fromEntries(['kind', 'previewMode', 'photoPreviewMode'].filter(key => instance?.[key] !== undefined || placement[key] !== undefined).map(key => [key, instance?.[key] ?? placement[key]])),
        id: asText(instance?.id || `${placementId}-zone`),
        placementId,
        parts: Array.isArray(instance?.parts) ? instance.parts : [],
      } : null));
      const image = normalizeImageUrl(view.image || view.imageUrl);
      if (!sourceViewId || !image || !zone) return options;

      options.push({
        key: placementId,
        sourceViewId,
        label: asText(placement.label || adaptedZone?.label || view.label || placementId),
        alt: asText(view.alt || placement.label || placementId),
        image,
        zones: [{ ...zone, placementId: zone.placementId || placementId,
          ...Object.fromEntries(['kind', 'previewMode', 'photoPreviewMode'].filter(key => zone[key] !== undefined || instance?.[key] !== undefined || placement[key] !== undefined)
            .map(key => [key, zone[key] ?? instance?.[key] ?? placement[key]])) }],
      });
      return options;
    }, []).map(normalizeViewOption).filter(Boolean);
  };

  const viewMatchesReference = (view, reference) => referencesMatch(reference, [
    view?.key,
    view?.viewId,
    view?.sourceViewId,
    view?.zoneId,
    view?.label,
    ...(Array.isArray(view?.zones) ? view.zones.flatMap((zone) => [zone.id, zone.placementId, zone.label]) : []),
  ]);

  const findMatchingView = (views, references = []) => {
    const validReferences = references.map(asText).filter(Boolean);
    return views.find((view) => validReferences.some((reference) => viewMatchesReference(view, reference))) || null;
  };

  const collectIndexedAssets = (properties, prefix) => {
    const assets = [];
    for (let index = 1; index <= MAX_ASSETS; index += 1) {
      const key = `${prefix}_${index}`;
      const src = normalizeImageUrl(properties?.[key]);
      if (!src) continue;
      assets.push({
        key,
        index,
        src,
        zoneId: asText(properties?.[`${key}_zone_id`]),
        viewId: asText(properties?.[`${key}_view_id`]),
      });
    }
    return assets;
  };

  const findMockupForView = (properties, view, viewCount) => {
    const mockups = collectIndexedAssets(properties, '_tm_branding_mockup');
    const exact = mockups.find((asset) => (
      (!asset.viewId || viewMatchesReference(view, asset.viewId)) &&
      (!asset.zoneId || viewMatchesReference(view, asset.zoneId)) &&
      Boolean(asset.viewId || asset.zoneId)
    ));
    if (exact) return exact.src;
    return mockups.length === 1 && viewCount === 1 ? mockups[0].src : '';
  };

  const findManifestView = (manifest, view) => {
    if (!manifest) return null;
    const isV2 = Number(manifest.version) === 2;
    return manifest.views.find((entry, index) => {
      const key = asText(isV2 ? entry?.viewId : (entry?.key || `view-${index + 1}`));
      return viewMatchesReference(view, key) || viewMatchesReference(view, entry?.sourceViewId) ||
        viewMatchesReference(view, entry?.zoneId);
    }) || null;
  };

  const getFileName = (value) => {
    const clean = asText(value).split(/[?#]/u)[0];
    const encoded = clean.split('/').pop() || '';
    try {
      return decodeURIComponent(encoded).toLowerCase();
    } catch (error) {
      return encoded.toLowerCase();
    }
  };

  const collectDesignAssets = (properties) => [
    ...collectIndexedAssets(properties, '_tm_branding_design'),
    ...collectIndexedAssets(properties, '_tm_studio_artwork'),
  ];

  const resolveDesignAsset = (properties, artwork, view) => {
    const assets = collectDesignAssets(properties);
    const fileName = getFileName(artwork?.fileName);
    const byFile = fileName ? assets.find((asset) => getFileName(asset.src) === fileName) : null;
    if (byFile) return byFile;
    const byReference = assets.find((asset) => (
      (!asset.viewId || viewMatchesReference(view, asset.viewId)) &&
      (!asset.zoneId || viewMatchesReference(view, asset.zoneId)) &&
      Boolean(asset.viewId || asset.zoneId)
    ));
    if (byReference) return byReference;
    return assets.length === 1 ? assets[0] : null;
  };

  const isUnfoldedSurface = value => value && [value.kind, value.previewMode, value.photoPreviewMode, value.id, value.key, value.placementId, value.zoneId, value.zone, value.view]
    .some(item => ['360', 'wrap', 'unfolded_360', 'tour_complet', 'marquage_360'].includes(normalizeReference(item)));
  const isSupplierPlane = value => value && [value.kind, value.previewMode, value.photoPreviewMode]
    .some(item => ['supplier_plane', 'supplier_print_plane'].includes(normalizeReference(item)));
  const unavailablePlanePreview = (properties, view) => {
    const transform = parseJsonObject(properties._tm_studio_transform || properties._studio_artwork_transform);
    for (const artwork of Array.isArray(transform?.artworks) ? transform.artworks : []) {
      if (!isObject(artwork)) continue;
      const viewRef = asText(artwork.view || properties._tm_studio_view_id || properties._studio_view);
      const zoneRef = asText(artwork.zone || properties._tm_studio_zone_id || properties._studio_zone_key);
      if ((viewRef && !viewMatchesReference(view, viewRef)) || (zoneRef && !viewMatchesReference(view, zoneRef))) continue;
      const references = [artwork, view, ...(view.zones || [])];
      if (references.some(isUnfoldedSurface)) return 'unfolded_surface_composite_missing';
      if (references.some(isSupplierPlane)) return 'supplier_plane_composite_missing';
    }
    return null;
  };

  const resolveOverlay = (properties = {}, options = {}) => {
    const view = options.view;
    if (!view || unavailablePlanePreview(properties, view)) return null;
    const transform = parseJsonObject(properties._tm_studio_transform || properties._studio_artwork_transform);
    if (!transform) return null;
    const requestedVariant = getVariantLookupKeys(options.variantId);
    const transformVariant = getVariantLookupKeys(transform.variantId);
    if (requestedVariant.length && transformVariant.length && !requestedVariant.some((key) => transformVariant.includes(key))) {
      return null;
    }

    const artworks = Array.isArray(transform.artworks) ? transform.artworks : [];
    const matchingArtworks = artworks.filter((candidate) => {
      if (!isObject(candidate)) return false;
      const viewReference = asText(candidate.view || properties._tm_studio_view_id || properties._studio_view);
      const zoneReference = asText(candidate.zone || properties._tm_studio_zone_id || properties._studio_zone_key);
      return (!viewReference || viewMatchesReference(view, viewReference)) &&
        (!zoneReference || viewMatchesReference(view, zoneReference));
    });
    if (matchingArtworks.length !== 1) return null;
    const artwork = matchingArtworks[0];

    const asset = resolveDesignAsset(properties, artwork, view);
    if (!asset) return null;
    const zoneReference = asText(artwork.zone || asset.zoneId || properties._tm_studio_zone_id || properties._studio_zone_key);
    const zones = Array.isArray(view.zones) ? view.zones : [];
    const zone = zones.find((candidate) => referencesMatch(zoneReference, [
      candidate.id,
      candidate.key,
      candidate.placementId,
      candidate.groupId,
      candidate.label,
    ])) || (zones.length === 1 && !zoneReference ? zones[0] : null);
    if (!zone) return null;
    const zoneRotation = Number(zone.rotation || zone.angle || 0);
    const zoneParts = Array.isArray(zone.parts) ? zone.parts : [];
    const unsupportedPart = zoneParts.length > 1 || zoneParts.some((part) => (
      Number(part?.rotation || part?.angle || 0) !== 0 ||
      Number(part?.curvature || 0) !== 0 ||
      asText(part?.shape).toLowerCase() === 'circle'
    ));
    if (!Number.isFinite(zoneRotation) || zoneRotation !== 0 || unsupportedPart) return null;

    const x = Number(artwork.x);
    const y = Number(artwork.y);
    const width = Number(artwork.width);
    const height = Number(artwork.height);
    const rotation = Number(artwork.rotation || 0);
    if (![x, y, width, height, rotation].every(Number.isFinite) || width <= 0 || height <= 0) return null;

    // Preserve the same source-to-frame placement used by the Studio. A cropped
    // frame must not squeeze the full original back inside the cart thumbnail.
    const frame = artwork.contentFrame == null ? null : window.TodayMerchStudioDesignRendering?.contentFrames?.normalize(artwork.contentFrame);
    if (artwork.contentFrame != null && !frame) return null;
    const media = {};
    for (const key of ['mediaWidth', 'mediaHeight', 'mediaLeft', 'mediaTop', 'cropTop', 'cropRight', 'cropBottom', 'cropLeft']) {
      const fallback = ['mediaWidth', 'mediaHeight'].includes(key) ? 100 : 0;
      const number = artwork[key] === undefined ? fallback : artwork[key];
      if (typeof number !== 'number' || !Number.isFinite(number) || Math.abs(number) > (frame && key.startsWith('media') ? 1e8 : 2000)
        || ['mediaWidth', 'mediaHeight'].includes(key) && number <= 0
        || key.startsWith('crop') && (number < 0 || number >= 1)) return null;
      media[key] = number;
    }
    if (media.cropLeft + media.cropRight >= 1 || media.cropTop + media.cropBottom >= 1) return null;
    const left = zone.x + (zone.width * (x - (width / 2))) / 100;
    const top = zone.y + (zone.height * (y - (height / 2))) / 100;
    const globalWidth = (zone.width * width) / 100;
    const globalHeight = (zone.height * height) / 100;
    if (![left, top, globalWidth, globalHeight].every(Number.isFinite) || globalWidth <= 0 || globalHeight <= 0) return null;

    return {
      src: asset.src,
      alt: asText(artwork.fileName || options.productTitle),
      viewId: asText(asset.viewId || artwork.view || view.sourceViewId || view.key),
      zoneId: asText(artwork.zone || asset.zoneId || view.zoneId || zone.placementId || zone.id),
      left: roundMetric(left),
      top: roundMetric(top),
      width: roundMetric(globalWidth),
      height: roundMetric(globalHeight),
      rotation: roundMetric(rotation),
      media,
    };
  };

  const mergeManifestViews = (properties, manifest, suppliedViews, options) => {
    if (!manifest) return suppliedViews;
    const isV2 = Number(manifest.version) === 2;
    const labelForId = typeof options.labelForId === 'function' ? options.labelForId : (id) => id;
    const productTitle = asText(options.productTitle);
    const seen = new Set();

    return manifest.views.slice(0, MAX_VIEWS).reduce((views, entry, index) => {
      if (!isObject(entry)) return views;
      const key = asText(isV2 ? entry.viewId : (entry.key || `view-${index + 1}`));
      if (!key || seen.has(key)) return views;
      const sourceViewId = asText(entry.sourceViewId || key);
      const matched = findMatchingView(suppliedViews, [key, sourceViewId, entry.zoneId]);
      const baseSrc = normalizeImageUrl(isV2 ? entry.fallbackImage : entry.image) || matched?.image || '';
      if (!baseSrc && !normalizeImageUrl(options.itemImage)) return views;
      const label = asText(isV2 ? labelForId(key) : (entry.label || entry.alt || matched?.label || key)) || key;
      seen.add(key);
      views.push({
        ...(matched || {}),
        key,
        viewId: key,
        sourceViewId,
        zoneId: asText(entry.zoneId || matched?.zoneId),
        label,
        alt: isV2 ? [productTitle, label].filter(Boolean).join(' - ') : asText(entry.alt || label),
        image: baseSrc || normalizeImageUrl(options.itemImage),
        zones: matched?.zones || [],
      });
      return views;
    }, []);
  };

  const resolvePreviewViews = (properties = {}, options = {}) => {
    const manifest = parseCartPreviewManifest(properties);
    let suppliedViews = (Array.isArray(options.viewOptions) && options.viewOptions.length
      ? options.viewOptions
      : buildViewOptionsFromConfig(options.studioConfig, options.variantId)
    ).map(normalizeViewOption).filter(Boolean).slice(0, MAX_VIEWS);
    if (!suppliedViews.length) {
      const itemImage = normalizeImageUrl(options.itemImage);
      const labelForId = typeof options.labelForId === 'function' ? options.labelForId : (id) => id;
      suppliedViews = collectIndexedAssets(properties, '_tm_branding_mockup').slice(0, MAX_VIEWS).map((mockup) => {
        const key = mockup.zoneId || mockup.viewId || `branding-mockup-${mockup.index}`;
        return normalizeViewOption({
          key,
          sourceViewId: mockup.viewId || key,
          zoneId: mockup.zoneId,
          label: labelForId(key),
          alt: [asText(options.productTitle), labelForId(key)].filter(Boolean).join(' - '),
          image: itemImage || mockup.src,
          directCompositeSrc: mockup.src,
          zones: [],
        });
      }).filter(Boolean);
    }
    const candidates = mergeManifestViews(properties, manifest, suppliedViews, options);
    const views = candidates.length ? candidates : suppliedViews;
    const itemImage = normalizeImageUrl(options.itemImage);

    return views.map((view) => {
      const manifestView = findManifestView(manifest, view);
      const previewProperty = asText(manifestView?.previewProperty);
      const manifestComposite = previewProperty ? normalizeImageUrl(properties[previewProperty]) : '';
      const directMockup = manifestComposite ? '' : normalizeImageUrl(view.directCompositeSrc);
      const mockup = manifestComposite || directMockup ? '' : findMockupForView(properties, view, views.length);
      const compositeSrc = manifestComposite || directMockup || mockup;
      const baseSrc = normalizeImageUrl(view.image) || itemImage;
      const previewUnavailableReason = !compositeSrc ? unavailablePlanePreview(properties, view) : null;
      const overlay = compositeSrc ? null : resolveOverlay(properties, {
        view,
        variantId: options.variantId,
        productTitle: options.productTitle,
      });
      return {
        key: view.key,
        viewId: view.viewId || view.key,
        sourceViewId: view.sourceViewId || view.key,
        zoneId: view.zoneId || '',
        label: view.label,
        alt: view.alt || asText(options.productTitle),
        src: compositeSrc || baseSrc,
        baseSrc,
        compositeSrc,
        customized: Boolean(compositeSrc),
        ...(previewUnavailableReason ? { previewStatus: 'unavailable', previewUnavailableReason,
          previewMessage: `${previewUnavailableReason === 'unfolded_surface_composite_missing' ? 'Aperçu du marquage 360° indisponible.' : 'Aperçu du marquage indisponible.'} Votre fichier et son placement sont conservés.` } : {}),
        overlay,
        ...(overlay ? {
          artworks: [{
            image: overlay.src,
            label: view.label || overlay.alt || asText(options.productTitle) || 'Logo',
            x: overlay.left,
            y: overlay.top,
            width: overlay.width,
            height: overlay.height,
            rotation: overlay.rotation || 0,
          }],
        } : {}),
        zones: view.zones || [],
      };
    }).filter((view) => view.src);
  };

  const resolveActiveViewId = (properties = {}, views = [], manifest = parseCartPreviewManifest(properties)) => {
    if (!views.length) return '';
    const transform = parseJsonObject(properties._tm_studio_transform || properties._studio_artwork_transform);
    const references = [
      manifest?.activeViewId,
      manifest?.activeKey,
      properties._tm_studio_zone_id,
      properties._tm_studio_view_id,
      properties._studio_zone_key,
      properties._studio_view,
      ...(Array.isArray(transform?.zones) ? transform.zones : []),
      ...(Array.isArray(transform?.views) ? transform.views : []),
      transform?.artworks?.[0]?.zone,
      transform?.artworks?.[0]?.view,
    ];
    return findMatchingView(views, references)?.key || views[0].key;
  };

  const resolveLinePreview = (properties = {}, options = {}) => {
    const views = resolvePreviewViews(properties, options);
    const activeViewId = resolveActiveViewId(properties, views);
    return {
      activeViewId,
      activeView: views.find((view) => view.key === activeViewId) || views[0] || null,
      views,
    };
  };

  const resolveLinePreviewState = (properties = {}, options = {}) => {
    const state = resolveLinePreview(properties, options);
    const views = state.views.map((view) => {
      const legacyView = {
        ...view,
        customized: Boolean(view.compositeSrc),
      };
      if (view.overlay) {
        legacyView.artworks = [{
          image: view.overlay.src,
          label: view.label || view.overlay.alt || asText(options.productTitle) || 'Logo',
          x: view.overlay.left,
          y: view.overlay.top,
          width: view.overlay.width,
          height: view.overlay.height,
          rotation: view.overlay.rotation || 0,
        }];
      } else {
        delete legacyView.artworks;
      }
      delete legacyView.overlay;
      return legacyView;
    });
    return {
      activeViewId: state.activeViewId,
      activeView: views.find((view) => view.key === state.activeViewId) || views[0] || null,
      views,
    };
  };

  const resolveCartPreviewViews = (properties = {}, options = {}) => resolvePreviewViews(properties, options).map((view) => ({
    key: view.key,
    ...(view.zoneId ? { zoneId: view.zoneId } : {}),
    label: view.label,
    alt: view.alt,
    src: view.src,
    customized: view.customized,
    ...(view.previewUnavailableReason ? { previewStatus: view.previewStatus, previewUnavailableReason: view.previewUnavailableReason, previewMessage: view.previewMessage } : {}),
  }));

  class StudioCartMedia extends HTMLElement {
    connectedCallback() {
      if (this.dataset.studioCartMediaReady === 'true') return;
      this.dataset.studioCartMediaReady = 'true';

      const propertiesNode = this.querySelector('[data-studio-cart-properties]');
      if (!propertiesNode) return;
      const properties = parseJsonObject(propertiesNode.textContent) || {};
      const labels = parseJsonObject(this.querySelector('[data-studio-cart-labels]')?.textContent) || {};
      const configNode = this.querySelector('[data-studio-cart-config]');
      const studioConfig = parseJsonObject(configNode?.textContent) || {};
      this.mediaLink = this.querySelector('[data-studio-cart-media-link]');
      if (!this.mediaLink) return;
      this.image = this.mediaLink.querySelector('img');

      const state = resolveLinePreview(properties, {
        studioConfig,
        variantId: this.dataset.variantId || configNode?.dataset.variantId || '',
        itemImage: this.image?.currentSrc || this.image?.src || '',
        labelForId: (id) => labels[id] || id,
        productTitle: this.dataset.productTitle || '',
      });
      this.views = state.views;
      if (!this.views.length) return;

      if (!this.image) {
        this.image = document.createElement('img');
        this.image.loading = 'lazy';
        this.image.decoding = 'async';
        this.image.className = 'studio-cart-media__image';
        this.mediaLink.append(this.image);
      }
      this.image.removeAttribute('srcset');
      this.image.removeAttribute('sizes');
      this.image.removeAttribute('is');
      this.image.addEventListener('load', () => this.queueOverlayStageUpdate());
      if (typeof ResizeObserver === 'function') {
        this.overlayResizeObserver = new ResizeObserver(() => this.queueOverlayStageUpdate());
        this.overlayResizeObserver.observe(this.mediaLink);
      }

      if (this.views.length > 1 && !this.closest('cart-drawer')) this.renderSwitcher();
      this.showView(state.activeViewId);
      this.classList.add('is-studio-ready');
    }

    disconnectedCallback() {
      this.overlayResizeObserver?.disconnect();
    }

    renderSwitcher() {
      const switcher = document.createElement('div');
      switcher.className = 'studio-cart-media__switcher';
      switcher.setAttribute('role', 'group');
      switcher.setAttribute('aria-label', this.dataset.viewLabel || '');

      this.views.forEach((view) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'studio-cart-media__switch';
        button.dataset.studioCartView = view.key;
        button.textContent = view.label;
        button.setAttribute('aria-pressed', 'false');
        button.addEventListener('click', () => this.showView(view.key));
        switcher.append(button);
      });

      this.append(switcher);
      this.switcher = switcher;
    }

    renderOverlay(overlay) {
      this.overlayElement?.remove();
      this.overlayElement = null;
      if (!overlay) return;

      const stage = document.createElement('span');
      stage.className = 'studio-cart-media__artwork-stage';
      const wrapper = document.createElement('span');
      wrapper.className = 'studio-cart-media__artwork';
      wrapper.style.setProperty('--studio-cart-artwork-x', `${overlay.left}%`);
      wrapper.style.setProperty('--studio-cart-artwork-y', `${overlay.top}%`);
      wrapper.style.setProperty('--studio-cart-artwork-width', `${overlay.width}%`);
      wrapper.style.setProperty('--studio-cart-artwork-height', `${overlay.height}%`);
      wrapper.style.setProperty('--studio-cart-artwork-rotation', `${overlay.rotation}deg`);
      const image = document.createElement('img');
      image.src = overlay.src;
      image.alt = overlay.alt || '';
      image.loading = 'lazy';
      image.decoding = 'async';
      if (overlay.media) {
        const media = overlay.media;
        wrapper.style.overflow = 'hidden';
        wrapper.style.clipPath = `inset(${media.cropTop * 100}% ${media.cropRight * 100}% ${media.cropBottom * 100}% ${media.cropLeft * 100}%)`;
        image.style.position = 'absolute';
        image.style.left = `${media.mediaLeft}%`; image.style.top = `${media.mediaTop}%`;
        image.style.width = `${media.mediaWidth}%`; image.style.height = `${media.mediaHeight}%`;
        image.style.maxWidth = 'none'; image.style.maxHeight = 'none';
        image.style.objectFit = 'contain';
      }
      wrapper.append(image);
      stage.append(wrapper);
      this.mediaLink.append(stage);
      this.overlayElement = stage;
      this.queueOverlayStageUpdate();
    }

    queueOverlayStageUpdate() {
      if (!this.overlayElement) return;
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => this.updateOverlayStage());
        return;
      }
      this.updateOverlayStage();
    }

    updateOverlayStage() {
      const stage = this.overlayElement;
      const naturalWidth = Number(this.image?.naturalWidth || 0);
      const naturalHeight = Number(this.image?.naturalHeight || 0);
      const box = this.mediaLink?.getBoundingClientRect?.();
      if (!stage || !naturalWidth || !naturalHeight || !box?.width || !box?.height) return;

      const imageRatio = naturalWidth / naturalHeight;
      const boxRatio = box.width / box.height;
      const width = imageRatio >= boxRatio ? box.width : box.height * imageRatio;
      const height = imageRatio >= boxRatio ? box.width / imageRatio : box.height;
      stage.style.left = `${(box.width - width) / 2}px`;
      stage.style.top = `${(box.height - height) / 2}px`;
      stage.style.width = `${width}px`;
      stage.style.height = `${height}px`;
    }

    showView(key) {
      const view = this.views.find((item) => item.key === key) || this.views[0];
      if (!view || !this.image) return;

      this.image.src = view.src;
      this.image.alt = view.alt || this.dataset.productTitle || '';
      this.image.hidden = false;
      this.mediaLink.dataset.studioCartCustomized = view.customized ? 'true' : 'false';
      this.renderOverlay(view.overlay);
      if (view.previewMessage && !this.previewNotice) {
        this.previewNotice = document.createElement('p'); this.previewNotice.className = 'studio-cart-media__notice';
        this.previewNotice.setAttribute('role', 'status'); this.append(this.previewNotice);
      }
      if (this.previewNotice) { this.previewNotice.textContent = view.previewMessage || ''; this.previewNotice.hidden = !view.previewMessage; }
      this.queueOverlayStageUpdate();

      this.switcher?.querySelectorAll('[data-studio-cart-view]').forEach((button) => {
        const isActive = button.dataset.studioCartView === view.key;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }
  }

  const canonicalApi = Object.freeze({
    MANIFEST_PROPERTY,
    LEGACY_MANIFEST_PROPERTY,
    normalizeImageUrl,
    parsePreviewManifest: parseCartPreviewManifest,
    parseCartPreviewManifest,
    buildViewOptionsFromConfig,
    resolveActiveViewId,
    resolveOverlay,
    resolvePreviewViews,
    resolveCartPreviewViews,
    resolveLinePreview,
    resolveLinePreviewState,
    StudioCartMedia,
  });
  window.TodayMerchStudioPreview = canonicalApi;

  if (!customElements.get('studio-cart-media')) {
    customElements.define('studio-cart-media', StudioCartMedia);
  }

  if (window.__TODAYMERCH_CART_STUDIO_PREVIEW_TEST__) {
    window.TodayMerchCartStudioPreviewTest = canonicalApi;
  }
})();
